import { useMemo } from "react";
import * as Yup from "yup";

import useErrorAlert from "../../../../../hooks/useErrorAlert";
import useLocation from "../../../../../hooks/useLocation";
import { register } from "../../../../../services/auth";
import { ApiProfile } from "../../../../../types";
import getApiErrorHasDetail from "../../../../../utils/getApiErrorHasDetail";
import getGenericErrorInfo from "../../../../../utils/getGenericErrorInfo";

const currentYear = new Date().getFullYear();
const MIN_BIRTH_YEAR = currentYear - 120;
const MIN_AGE = 10;
const MAX_BIRTH_YEAR = currentYear;

const getSchema = (
  isParent: boolean | undefined,
  isJudge: boolean | undefined
) => {
  const file = Yup.mixed<File>().test(
    "FILE_FORMAT",
    "Please select an image",
    (value) => value?.type?.startsWith("image/") ?? true
  );
  return Yup.object().shape({
    email: Yup.string().email("Invalid email").required("Required"),
    password: Yup.string().required("Required"),
    confirmedPassword: Yup.string()
      .required("Required")
      .oneOf([Yup.ref("password"), null], "Passwords must match"),
    firstName: Yup.string().required("Required"),
    lastName: Yup.string().required("Required"),
    didAcceptTermsOfUse: Yup.boolean()
      .required()
      .oneOf([true], "You must accept the terms to register"),
    didAcceptPrivacyPolicy: Yup.boolean()
      .required()
      .oneOf([true], "You must accept the privacy policy to register"),
    profilePicture: file.optional(),
    ...(!isParent && !isJudge
      ? {
          yearOfBirth: Yup.number()
            .typeError("Please enter a year")
            .positive()
            .integer()
            .required("Required")
            .min(MIN_BIRTH_YEAR, `Must be at least ${MIN_BIRTH_YEAR}`)
            .max(
              MAX_BIRTH_YEAR - MIN_AGE,
              "You must be over 10 to register yourself. If you aren’t, please get a parent/guardian to register you."
            ),
          gender: Yup.string()
            .required("Required")
            .oneOf(["MALE", "FEMALE", "OTHER"]),
        }
      : {}),
  });
};

type RegisterValues = {
  email: string;
  password: string;
  confirmedPassword: string;
  firstName: string;
  lastName: string;
  didAcceptTermsOfUse: boolean;
  didAcceptPrivacyPolicy: boolean;
  profilePicture?: File;
  yearOfBirth?: "" | number;
  gender?: string | undefined;
};

export default function useRegisterScreen({
  onRegister,
  isJudge,
}: {
  onRegister: (profile: ApiProfile) => void;
  isJudge: boolean;
}) {
  const location = useLocation<{
    email: string;
    isParent: boolean;
    yearOfBirth: number;
  }>();

  const email = location.state?.email;
  const isParent = location.state?.isParent;
  const yearOfBirthInitial = location.state?.yearOfBirth;

  const { setError } = useErrorAlert();

  const onSubmit = async (
    values: RegisterValues,
    { setFieldError }: { setFieldError: (field: string, error: string) => void }
  ) => {
    // if gender and year of birth are falsy, set them to undefined so they don't trip up API validation
    try {
      const profile = await register({
        ...values,
        yearOfBirth: values.yearOfBirth || undefined,
        gender: values.gender || undefined,
      });
      onRegister(profile);
    } catch (error) {
      if (getApiErrorHasDetail(error)) {
        if (error.info.detail.password) {
          setFieldError(
            "password",
            error.info.detail.password.map((x) => x.message).join("\n")
          );
        } else {
          setError(getGenericErrorInfo(error));
        }
      } else {
        setError(getGenericErrorInfo(error));
      }
    }
  };

  const schema = useMemo(
    () => getSchema(isParent, isJudge),
    [isParent, isJudge]
  );

  let introText = "";

  if (isParent) {
    introText =
      "Please give us your details so we can get in touch with your child’s results.";
  } else if (isJudge) {
    introText =
      "We just need a few of your details so you can get started with judging.";
  } else {
    introText = `We need some details to display you on the leaderboards and your score card. These will be visible to everyone, no silly names please!`;
  }

  return {
    initialValues: {
      email: email || "",
      password: "",
      confirmedPassword: "",
      firstName: "",
      lastName: "",
      yearOfBirth: yearOfBirthInitial || ("" as const),
      profilePicture: undefined,
      didAcceptTermsOfUse: false,
      didAcceptPrivacyPolicy: false,
      gender: "",
    },
    onSubmit,
    schema,
    email,
    isParent,
    isJudge,
    introText,
  };
}
